import React, { useState, useEffect, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { COMPONENT_TYPES, MENU_TYPES } from '../constants';
import SmartEditor2Component from './common/SmartEditor2Component';
import { 
  addMenu, 
  updateMenu, 
  removeMenu, 
  selectMenu, 
  reorderMenus,
  selectMenus, 
  selectSelectedMenuId
} from '../redux/editorSlice';
import { 
  selectGlobalMenus, 
  selectMainMenus, 
  saveMenus, 
  loadMenus,
  setGlobalMenus,
  setMainMenus
} from '../redux/WithcookieSlice';
import { AppstoreOutlined, ColumnWidthOutlined, BorderOutlined } from '@ant-design/icons';
import { SignupComponent, getModularizedComponent } from './module';

// 모듈화된 컴포넌트들을 import 합니다
import { 
  LoginComponent, 
  BoardComponent, 
  ProductDetailComponent, 
  ProductListComponent, 
  OrderHistoryComponent, 
  CompanyHistoryComponent, 
  AssetHistoryComponent, 
  MembershipSignupComponent,
  CustomMembershipSignupComponent,
  BinaryLegsComponent
} from './module';

const DraggableComponent = ({ type, name, icon, category }) => {

  // 컴포넌트 타입이 문자열인지 확인
  const typeStr = typeof type === 'string' ? type : String(type);
  
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'COMPONENT',
    item: () => {
      // 드래그 시작할 때 호출되는 함수
      
      return { 
        id: null, // 신규 컴포넌트임을 나타내는 null ID
        name,
        // 타입 정보 명확하게 전달
        type,
        componentType: type,
        category, // 카테고리 정보 추가
        // 디버깅용 정보
        _debug: {
          type,
          typeStr,
          category,
          isContainer: type === COMPONENT_TYPES.CONTAINER,
          isRow: type === COMPONENT_TYPES.ROW,
          isColumn: type === COMPONENT_TYPES.COLUMN,
          isText: type === COMPONENT_TYPES.TEXT,
          isImage: type === COMPONENT_TYPES.IMAGE,
          isButton: type === COMPONENT_TYPES.BUTTON,
          componentTypeValues: Object.values(COMPONENT_TYPES)
        }
      };
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className="draggable-component"
      style={{ opacity: isDragging ? 0.5 : 1 }}
      data-component-type={typeStr}
      data-category={category}
    >
      <div className="component-icon">{icon}</div>
      <div className="component-label">{name}</div>
      {/* 디버깅용 타입 표시 */}
      <div style={{ display: 'none' }}>{typeStr}</div>
    </div>
  );
};

const DropZone = ({ components, setComponents }) => {
  const [, drop] = useDrop(() => ({
    accept: "COMPONENT",
    drop: (item) => {

      
      // 컴포넌트 타입에 따라 고유 ID 생성
      let componentId;
      switch (item.type) {
        case COMPONENT_TYPES.LOGIN:
          componentId = `login-${Date.now()}`;
          break;
        case COMPONENT_TYPES.BOARD:
          componentId = `board-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PRODUCT_DETAIL:
          componentId = `product-detail-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PRODUCT_LIST:
          componentId = `product-list-${Date.now()}`;
          break;
        case COMPONENT_TYPES.SIGNUP:
          componentId = `signup-${Date.now()}`;
          break;
        case COMPONENT_TYPES.MAIN_BANNER:
          componentId = `main-banner-${Date.now()}`;
          break;
        case COMPONENT_TYPES.CART:
          componentId = `cart-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PAYMENT:
          componentId = `payment-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PAYLETTER_PAYMENT:
          componentId = `payletter-payment-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PAYMENT_SUCCESS:
          componentId = `payment-success-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PAYMENT_FAIL:
          componentId = `payment-fail-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PAYLETTER_SUCCESS:
          componentId = `payletter-success-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PAYLETTER_FAIL:
          componentId = `payletter-fail-${Date.now()}`;
          break;
        case COMPONENT_TYPES.FAQ:
          componentId = `faq-${Date.now()}`;
          break;
        case COMPONENT_TYPES.ORDER_HISTORY:
          componentId = `order-history-${Date.now()}`;
          break;
        case COMPONENT_TYPES.COMPANY_HISTORY:
          componentId = `company-history-${Date.now()}`;
          break;
        case COMPONENT_TYPES.MILEAGE_HISTORY:
          componentId = `mileage-history-${Date.now()}`;
          break;
        case COMPONENT_TYPES.ASSET_HISTORY:
          componentId = `asset-history-${Date.now()}`;
          break;
        case COMPONENT_TYPES.MEMBERSHIP_SIGNUP:
          componentId = `membership-signup-${Date.now()}`;
          break;
        case COMPONENT_TYPES.CUSTOM_MEMBERSHIP_SIGNUP:
          componentId = `custom-membership-signup-${Date.now()}`;
          break;
        case COMPONENT_TYPES.BINARY_LEGS:
          componentId = `binary-legs-${Date.now()}`;
          break;
        case COMPONENT_TYPES.UNILEVEL_LEGS:
          componentId = `unilevel-legs-${Date.now()}`;
          break;
        case COMPONENT_TYPES.RICH_EDITOR:
          componentId = `rich-editor-${Date.now()}`;
          break;
        case COMPONENT_TYPES.USER_PROFILE:
        case 'USER_PROFILE':
          componentId = `user-profile-${Date.now()}`;
          break;
        case COMPONENT_TYPES.ASSET_HISTORY:
          componentId = `asset-history-${Date.now()}`;
          break;
        case COMPONENT_TYPES.REVIEW_FORM:
          componentId = `review-form-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PRODUCT_SLIDER:
          componentId = `product-slider-${Date.now()}`;
          break;
        case COMPONENT_TYPES.IMAGE_SLIDER:
          componentId = `image-slider-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PRODUCT_LIST:
          componentId = `product-list-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PRODUCT_DETAIL:
          componentId = `product-detail-${Date.now()}`;
          break;
        case COMPONENT_TYPES.PRODUCT_LIST:
          componentId = `product-list-${Date.now()}`;
          break;
        
        default:
          componentId = `${item.type.toLowerCase()}-${Date.now()}`;
      }
      
      // default component data for FAQ
      let initialData = {};
      if (item.type === COMPONENT_TYPES.FAQ) {
        initialData = {
          faqItems: [
            { question: '쇼핑몰 배송은 얼마나 걸리나요?', answer: '일반적으로 2-3일 내에 배송이 완료됩니다.' },
            { question: '제품을 반품하고 싶습니다. 어떻게 해야 하나요?', answer: '고객센터에 문의하시면 반품 절차를 안내해 드립니다.' }
          ],
          title: '자주하는질문',
          style: {
            titleStyle: {
              textAlign: 'left',
              fontSize: '24px'
            }
          }
        };
      } else if (item.type === COMPONENT_TYPES.COMPANY_HISTORY) {
        initialData = {
          title: '회사 연혁',
          subtitle: '저희 회사의 성장 과정을 소개합니다',
          timelineStyle: 'vertical',
          colorScheme: {
            primary: '#3498db',
            secondary: '#f1c40f',
            background: '#ffffff',
            text: '#333333'
          },
          animation: true,
          historyItems: [
            {
              id: uuidv4(),
              year: '2010',
              title: '회사 설립',
              description: '혁신적인 비전을 가지고 회사가 설립되었습니다.'
            },
            {
              id: uuidv4(),
              year: '2015',
              title: '해외 시장 진출',
              description: '글로벌 시장으로 확장하여 아시아 지역에 첫 해외 지사를 설립했습니다.'
            },
            {
              id: uuidv4(),
              year: '2020',
              title: '기술 혁신상 수상',
              description: '혁신적인 기술력을 인정받아 국내 기술 혁신상을 수상했습니다.'
            },
            {
              id: uuidv4(),
              year: '2023',
              title: '신규 서비스 론칭',
              description: '고객 중심의 혁신적인 서비스를 성공적으로 출시했습니다.'
            }
          ]
        };
      } else if (item.type === COMPONENT_TYPES.RICH_EDITOR) {
        initialData = {
          content: '<p>이곳에 내용을 입력하세요.</p>'
        };
      }
      
      const newComponent = {
        id: componentId,
        type: item.type,
        content: item.name || "",
        ...initialData
      };
      setComponents((prev) => [...prev, newComponent]);
    },
  }));

  return (
    <div ref={drop} className="drop-zone">
      <h3>캔버스</h3>
      {components.map((component) => (
        <div key={component.id} className="dropped-component">
          {component.type === COMPONENT_TYPES.LOGIN ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.LOGIN), { id: component.id })
          ) : component.type === COMPONENT_TYPES.BOARD ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.BOARD), { id: component.id })
          ) : component.type === COMPONENT_TYPES.PRODUCT_DETAIL ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.PRODUCT_DETAIL), { 
              id: component.id, 
              componentProps: { 
                order: "", 
                none: "none", 
                lg: 12, 
                showDescriptionTab: true,
                showReviewsTab: true,
                showSpecificationTab: true
              }, 
              style: {width: "100%", maxWidth: "100%"} 
            })
          ) : component.type === COMPONENT_TYPES.PRODUCT_LIST ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.PRODUCT_LIST), { 
              id: component.id, 
              componentProps: { itemsPerRow: 4 } 
            })
          ) : component.type === COMPONENT_TYPES.SIGNUP ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.SIGNUP), { 
              id: component.id, 
              style: {width: "100%", maxWidth: "100%"} 
            })
          ) : component.type === COMPONENT_TYPES.MAIN_BANNER ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.MAIN_BANNER), { id: component.id })
          ) : component.type === COMPONENT_TYPES.ORDER_HISTORY ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.ORDER_HISTORY), { 
              id: component.id, 
              componentProps: { 
                className: "order-history-wrapper padding-tb-40",
                title: "주문 내역",
                emptyText: "주문 내역이 없습니다."
              } 
            })
          ) : component.type === COMPONENT_TYPES.COMPANY_HISTORY ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.COMPANY_HISTORY), { 
              id: component.id,
              title: component.title, 
              subtitle: component.subtitle,
              timelineStyle: component.timelineStyle,
              colorScheme: component.colorScheme,
              animation: component.animation,
              historyItems: component.historyItems
            })
          ) : component.type === COMPONENT_TYPES.ASSET_HISTORY ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.ASSET_HISTORY), { 
              id: component.id, 
              componentProps: {
                className: "asset-history-wrapper padding-tb-40",
                title: "자산 내역",
                emptyText: "자산 내역이 없습니다."
              }, 
              style: component.style, 
              isPreview: true 
            })
          ) : component.type === COMPONENT_TYPES.MEMBERSHIP_SIGNUP ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.MEMBERSHIP_SIGNUP), { 
              id: component.id, 
              componentProps: { 
                className: "membership-signup-wrapper",
                title: "멤버십 가입신청", 
                productIndex: 0,
                buttonText: "가입 신청",
                completedText: "가입 완료",
                description: "멤버십에 가입하시면 다양한 혜택을 받으실 수 있습니다."
              }, 
              style: {width: "100%", maxWidth: "100%"} 
            })
          ) : component.type === COMPONENT_TYPES.CUSTOM_MEMBERSHIP_SIGNUP ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.CUSTOM_MEMBERSHIP_SIGNUP), { 
              id: component.id, 
              componentProps: { 
                className: "custom-membership-signup-wrapper",
                title: "자유 멤버십 가입", 
                buttonText: "가입/추가 결제",
                completedText: "가입/추가 결제 완료",
                description: "직접 금액을 입력해 가입/추가 결제할 수 있습니다.",
                showRefundVisualization: true,
                refundLimitPercent: 400,
                minAmount: 10000,
                allowRejoin: true
              }, 
              style: {width: "100%", maxWidth: "100%"}, 
              isPreview: true 
            })
          ) : component.type === COMPONENT_TYPES.BINARY_LEGS ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.BINARY_LEGS), { 
              id: component.id, 
              componentProps: { 
                title: "후원 레그"
              }, 
              style: {width: "100%", maxWidth: "100%"} 
            })
          ) : component.type === COMPONENT_TYPES.UNILEVEL_LEGS ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.UNILEVEL_LEGS), { 
              id: component.id, 
              componentProps: { 
                title: "추천 레그"
              }, 
              style: {width: "100%", maxWidth: "100%"} 
            })
          ) : component.type === COMPONENT_TYPES.RICH_EDITOR ? (
            <SmartEditor2Component id={component.id} />
          ) : component.type === COMPONENT_TYPES.REVIEW_FORM ? (
            <div>리뷰 작성 폼 컴포넌트</div>
          ) : component.type === COMPONENT_TYPES.FAQ ? (
            <p>{component.content}</p>
          ) : component.type === COMPONENT_TYPES.CART ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.CART), { 
              id: component.id, 
              componentProps: { 
                className: "cart-wrapper padding-tb-40",
                title: "장바구니",
                emptyText: "장바구니가 비어있습니다."
              } 
            })
          ) : component.type === COMPONENT_TYPES.PAYMENT ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.PAYMENT), { 
              id: component.id, 
              componentProps: { 
                className: "payment-wrapper padding-tb-40",
                title: "결제"
              } 
            })
          ) : component.type === COMPONENT_TYPES.PAYLETTER_PAYMENT ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.PAYLETTER_PAYMENT), { 
              id: component.id, 
              componentProps: { 
                className: "payletter-payment-wrapper",
                title: "PayLetter 결제",
                description: "안전하고 간편한 PayLetter 결제를 진행해주세요.",
                buttonText: "결제하기",
                pgcode: "mobile",
                showDescription: true
              },
              style: {width: "100%", maxWidth: "600px", margin: "0 auto"}
            })
          ) : component.type === COMPONENT_TYPES.PAYMENT_SUCCESS ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.PAYMENT_SUCCESS), { 
              id: component.id, 
              componentProps: { 
                className: "payment-success-wrapper padding-tb-40",
                title: "결제 성공"
              } 
            })
          ) : component.type === COMPONENT_TYPES.PAYMENT_FAIL ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.PAYMENT_FAIL), { 
              id: component.id, 
              componentProps: { 
                className: "payment-fail-wrapper padding-tb-40",
                title: "결제 실패"
              } 
            })
          ) : component.type === COMPONENT_TYPES.PAYLETTER_SUCCESS ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.PAYLETTER_SUCCESS), { 
              id: component.id, 
              componentProps: { 
                className: "payletter-success-container",
                title: "PayLetter 결제 성공",
                successMessage: "PayLetter 결제가 성공적으로 처리되었습니다."
              },
              isEditor: true
            })
          ) : component.type === COMPONENT_TYPES.PAYLETTER_FAIL ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.PAYLETTER_FAIL), { 
              id: component.id, 
              componentProps: { 
                className: "payletter-fail-container",
                title: "PayLetter 결제 실패",
                failMessage: "결제가 취소되었거나 실패했습니다."
              },
              isEditor: true
            })
          ) : component.type === COMPONENT_TYPES.IMAGE_SLIDER ? (
            React.createElement(getModularizedComponent(COMPONENT_TYPES.IMAGE_SLIDER), { 
              id: component.id, 
              componentProps: { 
                sliderTitle: "이미지 슬라이더",
                showTitle: false,
                sliderHeight: "400px",
                slidesToShow: 1,
                autoSlide: true,
                autoSlideInterval: 4000,
                infiniteSlide: true,
                showNavigation: true,
                showIndicators: true,
                imageObjectFit: "cover",
                images: [
                  {
                    id: '1',
                    url: 'https://via.placeholder.com/800x400/4285f4/ffffff?text=Sample+Image+1',
                    title: '샘플 이미지 1',
                    alt: '샘플 이미지 1',
                    link: '',
                    openInNewTab: false
                  },
                  {
                    id: '2',
                    url: 'https://via.placeholder.com/800x400/34a853/ffffff?text=Sample+Image+2',
                    title: '샘플 이미지 2',
                    alt: '샘플 이미지 2',
                    link: '',
                    openInNewTab: false
                  }
                ]
              },
              style: {width: "100%", maxWidth: "100%"}
            })
          ) : (
            <p>{component.content}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// 메뉴 아이템 컴포넌트
const MenuItem = ({ menu, depth = 0, onMenuClick, onMenuDoubleClick, isSelected, children }) => {
  const dispatch = useDispatch();
  const maxDepth = 3; // 최대 깊이 제한
  const menus = useSelector(selectMenus);
  const [dropPosition, setDropPosition] = useState(null); // 'before', 'after', 'child' 중 하나
  
  // 드래그 소스 설정
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'MENU_ITEM',
    item: { id: menu.id, depth, type: menu.type },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));
  
  // 드롭 대상 설정
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: 'MENU_ITEM',
    hover: (item, monitor) => {
      if (item.id === menu.id) return; // 자기 자신에게는 적용하지 않음
      
      // 드래그 중인 아이템과 현재 아이템이 같은 타입인지 확인
      const sourceMenu = menus.find(m => m.id === item.id);
      if (sourceMenu.type !== menu.type) return;
      
      const hoverBoundingRect = document.getElementById(`menu-item-${menu.id}`).getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      
      // 위치에 따라 드롭 위치 결정
      if (hoverClientY < hoverMiddleY / 2) {
        // 위쪽 25% 영역: 이전 메뉴로 추가
        setDropPosition('before');
      } else if (hoverClientY > hoverMiddleY * 1.5) {
        // 아래쪽 25% 영역: 다음 메뉴로 추가
        setDropPosition('after');
      } else {
        // 중간 50% 영역: 하위 메뉴로 추가
        setDropPosition('child');
      }
    },
    canDrop: (item) => {
      // 자기 자신에게는 드롭 불가
      if (item.id === menu.id) return false;
      
      // 다른 타입의 메뉴 간에는 이동 불가
      if (item.type !== menu.type) return false;
      
      // 최대 깊이를 초과하는 경우 하위 메뉴로의 드롭 불가
      if (dropPosition === 'child' && depth >= maxDepth - 1) return false;
      
      return true;
    },
    drop: (item, monitor) => {
      // 이미 다른 곳에서 드롭된 경우 처리하지 않음
      if (monitor.didDrop()) {
        return;
      }
      
      // 메뉴 아이템 이동 처리
      if (dropPosition === 'child') {
        // 하위 메뉴로 이동
        dispatch(reorderMenus({
          sourceId: item.id, 
          destinationId: menu.id, 
          asChild: true
        }));
      } else if (dropPosition === 'before' || dropPosition === 'after') {
        // 같은 레벨에서 순서 변경
        const sourceMenu = menus.find(m => m.id === item.id);
        const sameParentMenus = menus.filter(m => 
          m.parentId === menu.parentId && 
          m.type === menu.type && 
          m.id !== item.id
        ).sort((a, b) => (a.order || 0) - (b.order || 0));
        
        // 대상 메뉴의 인덱스
        const targetIndex = sameParentMenus.findIndex(m => m.id === menu.id);
        
        // 순서 재정렬을 위해 업데이트 - 객체를 직접 수정하지 않고 액션으로 처리
        dispatch(reorderMenus({
          sourceId: item.id,
          destinationId: menu.id,
          asChild: false,
          position: dropPosition,
          targetParentId: menu.parentId // 목표 부모 ID를 전달
        }));
      }
      
      // 드롭 위치 초기화
      setDropPosition(null);
      
      return { id: menu.id, position: dropPosition };
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver({ shallow: true }),
      canDrop: !!monitor.canDrop(),
    }),
  }), [menu, depth, dropPosition, menus]);
  
  // 드래그 종료 시 상태 초기화
  useEffect(() => {
    if (!isOver) {
      setDropPosition(null);
    }
  }, [isOver]);
  
  // 메뉴 삭제 처리
  const handleRemove = (e) => {
    e.stopPropagation();
    if (window.confirm('이 메뉴를 삭제하시겠습니까?')) {
      dispatch(removeMenu(menu.id));
    }
  };
  
  // 드롭 위치에 따른 스타일
  const getDropStyle = () => {
    if (!isOver || !canDrop) return {};
    
    if (dropPosition === 'child') {
      return {
        backgroundColor: 'rgba(67, 97, 238, 0.1)',
        boxShadow: 'inset 0 0 0 2px rgba(67, 97, 238, 0.5)',
      };
    }
    
    return {};
  };
  
  return (
    <div ref={drop} style={{ opacity: isDragging ? 0.5 : 1, position: 'relative' }}>
      {isOver && canDrop && dropPosition === 'before' && (
        <div 
          className="menu-drop-indicator" 
          style={{ 
            position: 'absolute', 
            top: '-2px', 
            left: '0', 
            right: '0', 
            height: '2px', 
            backgroundColor: '#4361ee',
            transform: 'translateY(-50%)',
            zIndex: 2
          }} 
        />
      )}
      
      <div 
        id={`menu-item-${menu.id}`}
        ref={drag}
        className={`menu-item ${isSelected ? 'selected' : ''}`}
        onClick={() => onMenuClick(menu.id)}
        onDoubleClick={() => onMenuDoubleClick && onMenuDoubleClick(menu)}
        style={{ 
          marginLeft: `${depth * 20}px`,
          backgroundColor: isSelected ? 'rgba(67, 97, 238, 0.1)' : undefined,
          borderLeft: isSelected ? '2px solid #4361ee' : undefined,
          paddingLeft: isSelected ? '8px' : undefined,
          ...getDropStyle()
        }}
      >
        <span className="menu-name">{menu.name}</span>
        {menu.path && <span className="menu-path">{menu.path}</span>}
        <div className="menu-actions">
          <button 
            className="menu-action-btn"
            onClick={handleRemove}
            title="메뉴 삭제"
          >
            ✕
          </button>
        </div>
      </div>
      
      {isOver && canDrop && dropPosition === 'after' && (
        <div 
          className="menu-drop-indicator" 
          style={{ 
            position: 'absolute', 
            bottom: children ? 'auto' : '-2px', 
            left: '0', 
            right: '0', 
            height: '2px', 
            backgroundColor: '#4361ee',
            transform: 'translateY(50%)',
            zIndex: 2
          }} 
        />
      )}
      
      {children}
    </div>
  );
};

// 메뉴 트리 컴포넌트
const MenuTree = ({ type, onMenuDoubleClick }) => {
  const dispatch = useDispatch();
  const menus = useSelector(selectMenus);
  const selectedMenuId = useSelector(selectSelectedMenuId);
  
  // 해당 타입의 메뉴만 필터링
  const filteredMenus = menus.filter(menu => menu.type === type);
  
  // 신규 메뉴 추가 핸들러
  const handleAddMenu = () => {
    const newMenuId = uuidv4();
    // 기본 경로 설정 - 홈 메뉴가 없을 경우 첫 번째로 추가하는 메뉴는 홈("/")으로 설정
    let defaultPath = '';
    
    // 홈("/") 경로를 가진 메뉴가 있는지 확인
    const hasHomePath = menus.some(m => m.path === '/' && m.type === type);
    
    if (!hasHomePath && menus.filter(m => m.type === type && !m.parentId).length === 0) {
      // 홈 메뉴가 없고 첫 번째 메뉴인 경우 홈으로 설정
      defaultPath = '/';
    } else {
      // 그 외에는 기존 방식대로 UUID 기반 경로 생성
      defaultPath = type === MENU_TYPES.GLOBAL ? `/api/${newMenuId}` : `/${newMenuId}`;
    }
    
    dispatch(addMenu({
      id: newMenuId,
      name: defaultPath === '/' ? '홈' : `${type === MENU_TYPES.GLOBAL ? '글로벌' : '주'}메뉴`,
      path: defaultPath,
      type,
      parentId: null,
      order: menus.filter(m => m.type === type && !m.parentId).length,
      visible: true, // 모든 메뉴 타입을 기본적으로 표시하도록 변경
      components: []
    }));
    dispatch(selectMenu(newMenuId));
  };
  
  // 메뉴 선택 핸들러
  const handleMenuClick = (menuId) => {
    dispatch(selectMenu(menuId));
  };
  
  // 메뉴 더블클릭 핸들러
  const handleMenuDoubleClick = (menu) => {
    if (onMenuDoubleClick) {
      onMenuDoubleClick(menu);
    }
  };
  
  // 최상위 영역 드롭 핸들러 - 메뉴를 최상위로 이동
  const [{ isRootOver, canDropToRoot }, rootDrop] = useDrop(() => ({
    accept: 'MENU_ITEM',
    canDrop: (item) => {
      // 이미 최상위 메뉴인 경우 드롭 불가
      const sourceMenu = menus.find(m => m.id === item.id);
      return sourceMenu && sourceMenu.parentId !== null && item.type === type;
    },
    drop: (item) => {
      // 메뉴를 최상위로 이동
      dispatch(reorderMenus({
        sourceId: item.id,
        destinationId: 'root', // 특수값 'root'는 최상위 이동을 의미
        asChild: false
      }));
      return { isRoot: true };
    },
    collect: (monitor) => ({
      isRootOver: !!monitor.isOver(),
      canDropToRoot: !!monitor.canDrop(),
    }),
  }), [menus, type]);
  
  // 메뉴 트리 재귀적 렌더링 - 최대 3단계(0, 1, 2)까지 렌더링
  const renderMenuTree = (parentId = null, depth = 0) => {
    const childMenus = menus
      .filter(menu => menu.type === type && menu.parentId === parentId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    
    if (childMenus.length === 0) return null;
    
    return (
      <div className="menu-list" style={{ marginLeft: depth > 0 ? '20px' : '0' }}>
        {childMenus.map(menu => (
          <MenuItem 
            key={menu.id} 
            menu={menu} 
            depth={depth}
            onMenuClick={handleMenuClick}
            onMenuDoubleClick={handleMenuDoubleClick}
            isSelected={selectedMenuId === menu.id}
          >
            {depth < 2 && renderMenuTree(menu.id, depth + 1)}
          </MenuItem>
        ))}
      </div>
    );
  };
  
  const rootDropStyle = {
    backgroundColor: isRootOver && canDropToRoot ? '#f0f0f0' : 'transparent',
    border: isRootOver && canDropToRoot ? '2px dashed #1890ff' : '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '16px',
    marginBottom: '16px',
    transition: 'all 0.2s',
    minHeight: '80px'
  };
  
  // 최상위 메뉴 목록
  const rootMenus = menus.filter(menu => menu.type === type && !menu.parentId);
  
  return (
    <div className="menu-tree">
      <div className="menu-header">
        <h3>{type === MENU_TYPES.GLOBAL ? '글로벌 메뉴' : '주 메뉴'}</h3>
        <button onClick={handleAddMenu}>+ 메뉴추가</button>
      </div>
      <div 
        ref={rootDrop} 
        style={rootDropStyle}
        className="menu-root-drop-area"
      >
        {renderMenuTree()}
        {isRootOver && canDropToRoot && (
          <div className="drop-placeholder">
            메뉴를 최상위로 이동
          </div>
        )}
        {!isRootOver && rootMenus.length === 0 && (
          <div className="empty-menu-message">
            메뉴가 없습니다. 메뉴를 추가하거나 드래그해서 이동하세요.
          </div>
        )}
      </div>
    </div>
  );
};

// 메뉴 편집 모달 컴포넌트
const MenuEditModal = ({ isOpen, onClose, menu, onSave }) => {
  const dispatch = useDispatch();
  const menus = useSelector(selectMenus);
  
  const [menuName, setMenuName] = useState('');
  const [menuPath, setMenuPath] = useState('');
  const [isPathError, setIsPathError] = useState(false);
  const [pathErrorMessage, setPathErrorMessage] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isNotLoggedIn, setIsNotLoggedIn] = useState(false);
  const [externalLink, setExternalLink] = useState('');
  const [menuAction, setMenuAction] = useState('');
  
  // 모달이 열릴 때 메뉴 데이터로 상태 초기화
  useEffect(() => {
    if (isOpen && menu) {
      setMenuName(menu.name || '');
      setMenuPath(menu.path || '');
      setIsPathError(false);
      setPathErrorMessage('');
      setIsLoggedIn(menu.is_logged || false);
      setIsNotLoggedIn(menu.is_not_logged || false);
      setExternalLink(menu.externalLink || '');
      setMenuAction(menu.menuAction || '');
    }
  }, [isOpen, menu]);
  
  // 경로 유효성 검사
  const validatePath = (path) => {
    // 'edit' 또는 'preview'가 포함된 경로 체크
    if (path.includes('/edit') || path.includes('/preview') || 
        path === '/edit' || path === '/preview') {
      setIsPathError(true);
      setPathErrorMessage('사용할 수 없는 경로입니다 (edit, preview)');
      return false;
    }
    
    // 경로 중복 검사
    if (path && path !== menu.path) {
      const isDuplicate = menus.some(m => 
        m.id !== menu.id && m.path === path
      );
      if (isDuplicate) {
        setIsPathError(true);
        setPathErrorMessage('중복된 경로입니다');
        return false;
      }
    }
    
    setIsPathError(false);
    setPathErrorMessage('');
    return true;
  };
  
  // 경로 변경 처리
  const handlePathChange = (e) => {
    const newPath = e.target.value;
    setMenuPath(newPath);
    validatePath(newPath);
  };
  
  // 외부 링크 변경 처리
  const handleExternalLinkChange = (e) => {
    const newLink = e.target.value;
    setExternalLink(newLink);
    
    // 외부 링크 설정 시 메뉴 액션 초기화
    if (newLink && menuAction) {
      setMenuAction('');
    }
  };
  
  // 메뉴 액션 변경 처리
  const handleMenuActionChange = (e) => {
    const newAction = e.target.value;
    setMenuAction(newAction);
    
    // 메뉴 액션 설정 시 외부 링크 초기화
    if (newAction && externalLink) {
      setExternalLink('');
    }
  };
  
  // 로그인 상태 체크박스 변경 처리
  const handleIsLoggedInChange = (e) => {
    const newValue = e.target.checked;
    setIsLoggedIn(newValue);
    
    // 로그인 필요 체크 시 비로그인 체크 해제
    if (newValue && isNotLoggedIn) {
      setIsNotLoggedIn(false);
    }
  };
  
  // 비로그인 상태 체크박스 변경 처리
  const handleIsNotLoggedInChange = (e) => {
    const newValue = e.target.checked;
    setIsNotLoggedIn(newValue);
    
    // 비로그인 체크 시 로그인 필요 체크 해제
    if (newValue && isLoggedIn) {
      setIsLoggedIn(false);
    }
  };
  
  // 저장 처리
  const handleSave = () => {
    if (isPathError) {
      return;
    }
    
    const updatedMenu = {
      ...menu,
      name: menuName,
      path: menuPath,
      is_logged: isLoggedIn,
      is_not_logged: isNotLoggedIn,
      externalLink: externalLink,
      menuAction: menuAction
    };
    
    dispatch(updateMenu({
      id: menu.id,
      changes: updatedMenu
    }));
    
    if (onSave) {
      onSave(updatedMenu);
    }
    
    onClose();
  };
  
  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  
  if (!isOpen || !menu) {
    return null;
  }
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="menu-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>메뉴 편집</h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="form-group">
            <label>메뉴 이름 *</label>
            <input 
              type="text" 
              value={menuName} 
              onChange={(e) => setMenuName(e.target.value)}
              placeholder="메뉴 이름을 입력하세요"
              className="form-input"
            />
          </div>
          
          <div className="form-group">
            <label>라우팅 경로</label>
            <input 
              type="text" 
              value={menuPath} 
              onChange={handlePathChange}
              placeholder="/경로를 입력하세요"
              className={`form-input ${isPathError ? 'error' : ''}`}
              disabled={externalLink || menuAction ? true : false}
            />
            {isPathError && (
              <p className="error-text">{pathErrorMessage}</p>
            )}
            <p className="help-text">
              예: /about, /products/detail<br/>
              <strong>가변 경로:</strong> /products/:id, /category/:categoryId
            </p>
          </div>
          
          <div className="form-group">
            <label>외부 링크</label>
            <input 
              type="text" 
              value={externalLink} 
              onChange={handleExternalLinkChange}
              placeholder="https://외부링크를 입력하세요"
              className="form-input"
              disabled={menuAction ? true : false}
            />
            <p className="help-text">
              외부 링크를 설정하면 내부 라우팅 경로는 무시됩니다.
            </p>
          </div>
          
          <div className="form-group">
            <label>메뉴 액션</label>
            <select 
              value={menuAction} 
              onChange={handleMenuActionChange}
              className="form-select"
              disabled={externalLink ? true : false}
            >
              <option value="">-- 액션 없음 --</option>
              <option value="logout">로그아웃</option>
            </select>
            <p className="help-text">
              메뉴 액션을 설정하면 내부 라우팅과 외부 링크는 무시됩니다.
            </p>
          </div>
          
          <div className="form-group">
            <label>표시 조건</label>
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input 
                  type="checkbox" 
                  id="is_logged" 
                  checked={isLoggedIn} 
                  onChange={handleIsLoggedInChange}
                />
                <label htmlFor="is_logged">로그인 시에만 표시</label>
              </div>
              <div className="checkbox-item">
                <input 
                  type="checkbox" 
                  id="is_not_logged" 
                  checked={isNotLoggedIn} 
                  onChange={handleIsNotLoggedInChange}
                />
                <label htmlFor="is_not_logged">로그아웃 시에만 표시</label>
              </div>
            </div>
            <p className="help-text">
              체크하지 않으면 항상 표시됩니다. 두 옵션은 동시에 선택할 수 없습니다.
            </p>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>취소</button>
          <button 
            className="btn-save" 
            onClick={handleSave}
            disabled={isPathError || !menuName.trim()}
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
};

// 메뉴 속성 편집 패널 (간소화된 버전)
const MenuPropertyPanel = () => {
  const selectedMenuId = useSelector(selectSelectedMenuId);
  const menus = useSelector(selectMenus);
  const selectedMenu = menus.find(menu => menu.id === selectedMenuId);
  
  if (!selectedMenu) {
    return (
      <div className="menu-property-panel">
        <p className="no-menu-selected">메뉴를 클릭하여 선택하세요.</p>
      </div>
    );
  }
  
  return (
    <div className="menu-property-panel">
      <h4 className="property-title">선택된 메뉴</h4>
      <div className="selected-menu-info">
        <p><strong>이름:</strong> {selectedMenu.name}</p>
        <p><strong>경로:</strong> {selectedMenu.path || '설정되지 않음'}</p>
        <p><strong>타입:</strong> {selectedMenu.type === 'GLOBAL' ? '글로벌 메뉴' : '주 메뉴'}</p>
        {selectedMenu.externalLink && (
          <p><strong>외부 링크:</strong> {selectedMenu.externalLink}</p>
        )}
        {selectedMenu.menuAction && (
          <p><strong>메뉴 액션:</strong> {selectedMenu.menuAction}</p>
        )}
        {(selectedMenu.is_logged || selectedMenu.is_not_logged) && (
          <p><strong>표시 조건:</strong> 
            {selectedMenu.is_logged ? '로그인 시에만' : ''}
            {selectedMenu.is_not_logged ? '로그아웃 시에만' : ''}
          </p>
        )}
      </div>
      <p className="edit-instruction">메뉴를 더블클릭하여 편집하세요.</p>
    </div>
  );
};

// 메뉴 관리 탭 컴포넌트
const MenuManagementTab = () => {
  const dispatch = useDispatch();
  const menus = useSelector(selectMenus);
  const selectedMenuId = useSelector(selectSelectedMenuId);
  const selectedMenu = menus.find(menu => menu.id === selectedMenuId);
  
  // 메뉴 편집 모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMenu, setModalMenu] = useState(null);
  
  // Withcookie 슬라이스의 글로벌/주 메뉴 상태
  const globalMenus = useSelector(selectGlobalMenus);
  const mainMenus = useSelector(selectMainMenus);
  
  // 메뉴 더블클릭 핸들러
  const handleMenuDoubleClick = (menu) => {
    setModalMenu(menu);
    setIsModalOpen(true);
  };
  
  // 모달 닫기 핸들러
  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalMenu(null);
  };
  
  // 모달 저장 핸들러
  const handleModalSave = (updatedMenu) => {
    // 메뉴가 이미 dispatch를 통해 업데이트되므로 추가 작업은 필요 없음
  };
  
  // 메뉴 상태 변경 추적을 위한 ref
  const prevMenusRef = useRef({ global: [], main: [] });
  const syncInProgressRef = useRef(false);
  
  // 컴포넌트가 마운트될 때 서버에서 메뉴 데이터 로드
  useEffect(() => {
    dispatch(loadMenus());
  }, [dispatch]);
  
  // 메뉴 상태가 변경될 때 즉시 저장
  useEffect(() => {
    // 이미 진행 중인 동기화가 있다면 중복 실행 방지
    if (syncInProgressRef.current) {
      return;
    }

    const handleMenuChange = () => {
      // 글로벌 메뉴와 주 메뉴 추출
      const currentGlobalMenus = menus.filter(menu => menu.type === MENU_TYPES.GLOBAL);
      const currentMainMenus = menus.filter(menu => menu.type === MENU_TYPES.MAIN);
      
      // 이전 메뉴와 현재 메뉴를 비교하여 변경사항이 있는지 확인
      const globalMenusChanged = areMenuArraysChanged(prevMenusRef.current.global || [], currentGlobalMenus);
      const mainMenusChanged = areMenuArraysChanged(prevMenusRef.current.main || [], currentMainMenus);
      
      if (!globalMenusChanged && !mainMenusChanged) {
        return;
      }
      
      
      // 동기화 진행 중 플래그 설정
      syncInProgressRef.current = true;
      
      try {
        // withcookie 상태 직접 업데이트
        dispatch(setGlobalMenus(currentGlobalMenus));
        dispatch(setMainMenus(currentMainMenus));
        
        // 서버에 저장
        dispatch(saveMenus({
          globalMenus: currentGlobalMenus,
          mainMenus: currentMainMenus
        }));
        
        // 현재 메뉴 상태 저장
        prevMenusRef.current = {
          global: [...currentGlobalMenus],
          main: [...currentMainMenus]
        };
        
        
      } finally {
        // 동기화 완료 플래그 해제
        syncInProgressRef.current = false;
      }
    };
    
    // 메뉴 배열 변경 여부 비교 함수
    const areMenuArraysChanged = (prevMenus, currentMenus) => {
      if (prevMenus.length !== currentMenus.length) return true;
      
      // ID와 주요 속성 기반으로 간단한 비교
      const menuToString = menu => `${menu.id}-${menu.name}-${menu.path}-${menu.parentId}-${menu.order}-${menu.visible}`;
      const prevMenuStrs = prevMenus.map(menuToString).sort();
      const currentMenuStrs = currentMenus.map(menuToString).sort();
      
      for (let i = 0; i < prevMenuStrs.length; i++) {
        if (prevMenuStrs[i] !== currentMenuStrs[i]) return true;
      }
      
      return false;
    };
    
    // 모든 메뉴 변경에 반응
    handleMenuChange();
    
  }, [menus, dispatch]);
  
  // 컴포넌트 마운트 시 이전 메뉴 상태 초기화
  useEffect(() => {
    // 메뉴 상태 변경 추적을 위한 초기화
    prevMenusRef.current = {
      global: menus.filter(menu => menu.type === MENU_TYPES.GLOBAL),
      main: menus.filter(menu => menu.type === MENU_TYPES.MAIN)
    };
    
    return () => {
      // 컴포넌트 언마운트 시 동기화 플래그 초기화
      syncInProgressRef.current = false;
    };
  }, []);
  
  return (
    <div className="menu-management-tab">
      <h3 className="section-title">사이트 메뉴 설정</h3>
      <p className="section-description">사이트의 메뉴를 설정합니다. 글로벌 메뉴와 주 메뉴로 구분되며, 각 메뉴는 3단계까지 구성할 수 있습니다.</p>
      
      <div className="menu-section">
        <div className="menu-section-header">
          <h4 className="menu-type-title">글로벌 메뉴</h4>
          <p className="menu-type-description">사이트 최상단에 표시되는 메뉴입니다. 로그인, 회원가입, 마이페이지 등 주요 기능 링크를 설정하세요.</p>
        </div>
        <MenuTree type={MENU_TYPES.GLOBAL} onMenuDoubleClick={handleMenuDoubleClick} />
      </div>
      
      <hr className="menu-section-divider" />
      
      <div className="menu-section">
        <div className="menu-section-header">
          <h4 className="menu-type-title">주 메뉴</h4>
          <p className="menu-type-description">사이트의 메인 네비게이션 메뉴입니다. 콘텐츠의 주요 카테고리를 설정하세요.</p>
        </div>
        <MenuTree type={MENU_TYPES.MAIN} onMenuDoubleClick={handleMenuDoubleClick} />
      </div>
      
      {/*<div className="menu-property-section">*/}
      {/*  <h4 className="menu-property-title">메뉴 속성 설정</h4>*/}
      {/*  <MenuPropertyPanel />*/}
      {/*</div>*/}
      
      {/* 메뉴 편집 모달 */}
      <MenuEditModal 
        isOpen={isModalOpen}
        onClose={handleModalClose}
        menu={modalMenu}
        onSave={handleModalSave}
      />
    </div>
  );
};

// 탭 네비게이션 컴포넌트
const NavButtons = ({ activeTab, setActiveTab }) => {
  return (
    <div className="nav-buttons">

      <button 
        className={activeTab === 'advanced' ? "nav-button active" : "nav-button"} 
        onClick={() => setActiveTab('advanced')}
        title="고급 요소"
      >
        <i className="nav-icon">컴포넌트</i>
      </button>
      <button 
        className={activeTab === 'menu' ? "nav-button active" : "nav-button"} 
        onClick={() => setActiveTab('menu')}
        title="메뉴 관리"
      >
        <i className="nav-icon">메뉴 설정</i>
      </button>
    </div>
  );
};

function ComponentLibrary() {
  const [activeTab, setActiveTab] = useState('advanced');
  const [searchTerm, setSearchTerm] = useState('');

  
  // 고급 컴포넌트 탭
  const renderAdvancedComponents = () => {
    // 사용자 관련 컴포넌트 목록
    const userComponents = [
      <DraggableComponent
        key="user-login"
        type={COMPONENT_TYPES.LOGIN}
        name="로그인"
        icon={<i className="fas fa-sign-in-alt"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="user-signup"
        type={COMPONENT_TYPES.SIGNUP}
        name="회원가입"
        icon={<i className="fas fa-user-plus"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="user-membership"
        type={COMPONENT_TYPES.MEMBERSHIP_SIGNUP}
        name="멤버십 가입"
        icon={<i className="fas fa-user-tag"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="user-binary"
        type={COMPONENT_TYPES.BINARY_LEGS}
        name="바이너리 레그"
        icon={<i className="fas fa-sitemap"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="user-unilevel"
        type={COMPONENT_TYPES.UNILEVEL_LEGS}
        name="유니레벨 레그"
        icon={<i className="fas fa-network-wired"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="user-profile"
        type={COMPONENT_TYPES.USER_PROFILE}
        name="회원정보"
        icon={<i className="fas fa-user-circle"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="user-custom-membership"
        type={COMPONENT_TYPES.CUSTOM_MEMBERSHIP_SIGNUP}
        name="자유 멤버십"
        icon={<i className="fas fa-user-tag"></i>}
        category="advanced"
      />
    ];

    // 컨텐츠 관련 컴포넌트 목록
    const contentComponents = [
      <DraggableComponent
        key="content-board"
        type={COMPONENT_TYPES.BOARD}
        name="게시판"
        icon={<i className="fas fa-clipboard-list"></i>}
        category="advanced"
      />,
      
      <DraggableComponent
        key="content-banner"
        type={COMPONENT_TYPES.MAIN_BANNER}
        name="메인 배너"
        icon={<i className="fas fa-images"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="content-faq"
        type={COMPONENT_TYPES.FAQ}
        name="FAQ"
        icon={<i className="fas fa-question-circle"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="content-history"
        type={COMPONENT_TYPES.COMPANY_HISTORY}
        name="회사 연혁"
        icon={<i className="fas fa-building"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="content-editor"
        type={COMPONENT_TYPES.RICH_EDITOR}
        name="리치 에디터"
        icon={<i className="fas fa-edit"></i>}
        category="advanced"
      />,
      <DraggableComponent 
        key="basic-image-text"
        type={COMPONENT_TYPES.IMAGE_TEXT} 
        name="이미지-텍스트" 
        icon={<i className="fas fa-image"></i>}
        category="basic"
      />,
      <DraggableComponent 
        key="content-trending-items"
        type={COMPONENT_TYPES.TRENDING_ITEMS} 
        name="트렌딩 아이템" 
        icon={<i className="fas fa-chart-line"></i>}
        category="advanced"
      />,
      <DraggableComponent 
        key="content-fixed-bg-text"
        type={COMPONENT_TYPES.FIXED_BG_TEXT} 
        name="고정 배경 텍스트" 
        icon={<i className="fas fa-image"></i>}
        category="advanced"
      />
    ];

    // 상품 및 쇼핑 관련 컴포넌트 목록
    const shopComponents = [
      <DraggableComponent
        key="shop-products"
        type={COMPONENT_TYPES.PRODUCT_LIST}
        name="상품 목록"
        icon={<i className="fas fa-shopping-bag"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="shop-slider"
        type={COMPONENT_TYPES.PRODUCT_SLIDER}
        name="상품 슬라이더"
        icon={<i className="fas fa-sliders-h"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="image-slider"
        type={COMPONENT_TYPES.IMAGE_SLIDER}
        name="이미지 슬라이더"
        icon={<i className="fas fa-images"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="shop-detail"
        type={COMPONENT_TYPES.PRODUCT_DETAIL}
        name="상품 상세"
        icon={<i className="fas fa-shopping-bag"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="shop-cart"
        type={COMPONENT_TYPES.CART}
        name="장바구니"
        icon={<i className="fas fa-shopping-cart"></i>}
        category="advanced"
      />,
      // <DraggableComponent
      //   key="review-form"
      //   type={COMPONENT_TYPES.REVIEW_FORM}
      //   name="리뷰 작성"
      //   icon={<i className="fas fa-star"></i>}
      //   category="advanced"
      // />
    ];

    // 결제 및 주문 관련 컴포넌트 목록
    const paymentComponents = [
      <DraggableComponent
        key="payment-main"
        type={COMPONENT_TYPES.PAYMENT}
        name="결제"
        icon={<i className="fas fa-credit-card"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="payment-payletter"
        type={COMPONENT_TYPES.PAYLETTER_PAYMENT}
        name="PayLetter 결제"
        icon={<i className="fas fa-mobile-alt"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="payment-success"
        type={COMPONENT_TYPES.PAYMENT_SUCCESS}
        name="결제 성공"
        icon={<i className="fas fa-check-circle"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="payment-fail"
        type={COMPONENT_TYPES.PAYMENT_FAIL}
        name="결제 실패"
        icon={<i className="fas fa-times-circle"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="payletter-success"
        type={COMPONENT_TYPES.PAYLETTER_SUCCESS}
        name="PayLetter 성공"
        icon={<i className="fas fa-check-circle"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="payletter-fail"
        type={COMPONENT_TYPES.PAYLETTER_FAIL}
        name="PayLetter 실패"
        icon={<i className="fas fa-times-circle"></i>}
        category="advanced"
      />
    ];

    // 이력 및 내역 관련 컴포넌트 목록
    const historyComponents = [
      <DraggableComponent
        key="history-order"
        type={COMPONENT_TYPES.ORDER_HISTORY}
        name="주문 내역"
        icon={<i className="fas fa-history"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="history-mileage"
        type={COMPONENT_TYPES.ASSET_HISTORY}
        name="자산 내역"
        icon={<i className="fas fa-coins"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="history-benefit"
        type={COMPONENT_TYPES.BENEFIT_SUMMARY}
        name="혜택 요약"
        icon={<i className="fas fa-wallet"></i>}
        category="advanced"
      />,
      <DraggableComponent
        key="history-recommended"
        type={COMPONENT_TYPES.RECOMMENDED_USERS}
        name="추천 사용자"
        icon={<i className="fas fa-users"></i>}
        category="advanced"
      />
    ];

    // 내비게이션 관련 컴포넌트 목록
    const navigationComponents = [
      <DraggableComponent
        key="navigation-mobile-layout"
        type={COMPONENT_TYPES.MOBILE_LAYOUT}
        name="모바일 메뉴"
        icon="📱"
        category="navigation"
      />
    ];

    // 검색어가 있으면 모든 고급 컴포넌트를 합쳐서 필터링
    const allAdvancedComponents = [
      ...userComponents,
      ...contentComponents,
      ...shopComponents,
      ...paymentComponents,
      ...historyComponents,
      ...navigationComponents
    ];

    if (searchTerm) {
      const filteredComponents = filterComponents(allAdvancedComponents);
      
      if (filteredComponents.length === 0) {
        return (
          <div className="components-group">
            <h3>고급 컴포넌트</h3>
            <p>검색 결과가 없습니다.</p>
          </div>
        );
      }
      
      return (
        <div className="components-group">
          <h3>고급 컴포넌트 - 검색 결과</h3>
          <div className="components-grid">
            {filteredComponents}
          </div>
        </div>
      );
    }

    // 검색어가 없을 때는 카테고리별로 표시
    return (
      <div className="components-group">
        <h3>고급 컴포넌트</h3>
        
        {/* 사용자 관련 컴포넌트 */}
        <div className="component-category">
          <h4>사용자 관련</h4>
          <div className="components-grid">
            {userComponents}
          </div>
        </div>
        
        {/* 컨텐츠 관련 컴포넌트 */}
        <div className="component-category">
          <h4>컨텐츠</h4>
          <div className="components-grid">
            {contentComponents}
          </div>
        </div>
        
        {/* 상품 및 쇼핑 관련 컴포넌트 */}
        <div className="component-category">
          <h4>쇼핑 및 상품</h4>
          <div className="components-grid">
            {shopComponents}
          </div>
        </div>
        
        {/* 결제 및 주문 관련 컴포넌트 */}
        <div className="component-category">
          <h4>결제 및 주문</h4>
          <div className="components-grid">
            {paymentComponents}
          </div>
        </div>
        
        {/* 이력 및 내역 관련 컴포넌트 */}
        <div className="component-category">
          <h4>이력 및 내역</h4>
          <div className="components-grid">
            {historyComponents}
          </div>
        </div>
        
        {/* 내비게이션 관련 컴포넌트 */}
        <div className="component-category">
          <h4>내비게이션</h4>
          <div className="components-grid">
            {navigationComponents}
          </div>
        </div>
      </div>
    );
  };

  // 검색어에 따라 컴포넌트 필터링 함수
  const filterComponents = (components) => {
    if (!searchTerm) return components;
    
    // 검색어가 있으면 필터링 (대소문자 구분 없이)
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    // 검색 단어 분리
    const hasSpace = lowerSearchTerm.includes(' ');
    const searchWords = hasSpace 
      ? lowerSearchTerm.split(/\s+/).filter(word => word.length > 0)
      : [lowerSearchTerm];
    
    // 검색어가 없으면 모든 컴포넌트 반환
    if (searchWords.length === 0) return components;
    
    return components.filter(comp => {
      // 컴포넌트의 모든 속성을 검색 대상으로 설정
      const name = comp.props.name || '';
      const category = comp.props.category || '';
      const type = comp.props.type?.toString() || '';
      
      // 띄어쓰기 제거한 이름
      const nameWithoutSpace = name.replace(/\s+/g, '').toLowerCase();
      
      // 일반 검색용 텍스트 (각 단어별 검색용)
      const individualWords = name.toLowerCase().split(/\s+/);
      
      // 전체 검색어가 띄어쓰기 없이 붙어있는 경우 (주문내역, 포인트내역 등)
      if (!hasSpace && searchWords[0].length > 1) {
        // 전체 이름이 정확히 일치하는지 확인
        if (nameWithoutSpace === searchWords[0]) {
          return true;
        }
        
        // 전체 이름에 검색어가 포함되는지 확인
        if (nameWithoutSpace.includes(searchWords[0])) {
          // 각 단어에 정확히 일치하는 검색어가 있는지 확인
          for (const word of individualWords) {
            if (word === searchWords[0]) {
              return true;
            }
          }
          
          // 이름에 검색어가 포함되는지 확인
          return nameWithoutSpace.includes(searchWords[0]);
        }
        
        // 카테고리나 타입에서 검색
        return category.toLowerCase().includes(searchWords[0]) || 
               type.toLowerCase().includes(searchWords[0]);
      } 
      
      // 단일 단어 검색이거나 띄어쓰기가 있는 검색어의 경우
      else {
        // 모든 검색 단어가 포함되어 있는지 확인
        return searchWords.every(word => {
          // 단어별로 검색
          return individualWords.some(nameWord => nameWord.includes(word)) ||
                 category.toLowerCase().includes(word) ||
                 type.toLowerCase().includes(word);
        });
      }
    });
  };

  // 활성 탭에 따라 내용 렌더링
  const renderActiveTabContent = () => {
    switch (activeTab) {

      case 'advanced':
        return renderAdvancedComponents();
      case 'menu':
        return <MenuManagementTab />;
      default:
        return null;
    }
  };

  // 검색어 변경 핸들러
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="component-library">
      <div className="component-library-header" style={{ gap: '12px' }}>
        <h3>컴포넌트 라이브러리</h3>
        <NavButtons activeTab={activeTab} setActiveTab={setActiveTab} />
        
        {/* 검색 기능 - 메뉴 탭이 아닐 때만 표시 */}
        {activeTab !== 'menu' && (
          <div className="component-search" style={{ width: '100%', marginTop: '8px' }}>
            <input
              type="text"
              placeholder="컴포넌트 검색..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="form-control"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
                fontSize: '0.9rem',
                transition: 'all 0.2s ease'
              }}
            />
          </div>
        )}
      </div>
      <div className="component-library-content">
        {renderActiveTabContent()}
      </div>
    </div>
  );
}

export default ComponentLibrary;